import React, { useEffect } from "react";
import { useFormik } from "formik";
import CancelBtn from "../CustomButtons/CancelBtn/CancelBtn";
import "./AddUser.css";
import { TextInput } from "./TextInput";
import { SelectInput } from "./SelectInput";
import { CheckboxGroupInput } from "./CheckboxGroupInput";
import { SelectOption } from "./AddUser.types";
import {
  addUserInitialValues,
  addUserValidationSchema,
  defaultRoleOptions,
  defaultClientOptions,
  STANDARD_USER_ROLE_NAME,
} from "./AddUser.constants";

interface AddUserFormProps {
  visible: boolean;
  onClose: () => void;
  heading?: string;
  submitLabel?: string;
  // Role/client lists come from the page (usually RBAC config + business
  // clients), so they're props rather than hardcoded here.
  roleOptions?: SelectOption[];
  clientOptions?: SelectOption[];
  initialValues?: typeof addUserInitialValues;
  validationSchema?: typeof addUserValidationSchema;
  onSubmit?: (values: typeof addUserInitialValues) => void;
  // Called when the refresh button next to the client search box is
  // clicked; the page is expected to re-fetch clientOptions from its source.
  onClientsRefresh?: () => void;
  isedit?: boolean;
  loadingOption?: boolean;
}

export const AddUserForm: React.FC<AddUserFormProps> = ({
  visible,
  onClose,
  heading = "Add New User",
  submitLabel = "Save & Invite",
  roleOptions = defaultRoleOptions,
  clientOptions = defaultClientOptions,
  initialValues = addUserInitialValues,
  validationSchema = addUserValidationSchema,
  onSubmit,
  onClientsRefresh,
  isedit = false,
  loadingOption,
}) => {
    // console.log("AddUserForm component loaded");
  // console.log(roleOptions);
  // console.log(clientOptions);

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      if (onSubmit) {
        await onSubmit({
          ...values,
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
        });
      } else {
        console.log("AddUserForm submitted:", {
          ...values,
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
        });
      }
    },
  });
  // Reset form whenever the popup is closed
  useEffect(() => {
    if (!visible) {
      formik.resetForm();
    }
  }, [visible]);

  if (!visible) return null;
  const handleNameChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "firstName" | "lastName"
  ) => {
    formik.handleChange(e);
    formik.setFieldTouched(field, true, false);
    formik.validateField(field);
  };

  return (
  <div className="addUser-overlay" onClick={onClose}>
    <div
      className="addUser-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <h2 className="addUser-heading">{heading}</h2>

      <div className="addUser-close">
        <CancelBtn CancelBtnClick={onClose} />
      </div>

      <form onSubmit={formik.handleSubmit}>
        <div className="addUser-row">
          <TextInput
            maxLength={50}
            label="First Name"
            name="firstName"
            placeholder="Enter First name"
            required
            formik={formik}
            //  inputProps={{ maxLength: 50 }}
               onChange={(e) => handleNameChange(e, "firstName")}

            
          />

          <TextInput
            maxLength={50}
            label="Last Name"
            name="lastName"
            placeholder="Enter Last name"
            required
            formik={formik}
            //  inputProps={{ maxLength: 50 }}
             onChange={(e) => handleNameChange(e, "lastName")}
          />
        </div>

        <div className="addUser-field">
          <TextInput
            label="Email Address"
            name="email"
            type="email"
            placeholder="Enter email address"
            required
            fullWidth
            formik={formik}
            disabled={isedit}
          />
        </div>

        <div className="addUser-field">
          <SelectInput
            label="Role"
            name="role"
            placeholder="Select Role"
            required
            options={roleOptions}
            formik={formik}
          />
        </div>

        {/* Only a Standard User gets assigned specific clients — other
        roles (Admin, AdminOwner, Client) have no use for this field. */}
        {formik.values.role === STANDARD_USER_ROLE_NAME && (
          <div className="addUser-field">
            <CheckboxGroupInput
              label="Assign Clients"
              name="client"
              helperText="Select at least one client for the standard User"
              options={clientOptions}
              formik={formik}
              searchable
              searchPlaceholder="Search clients..."
              onRefresh={onClientsRefresh}
              loadingOption={loadingOption}
            />
          </div>
        )}

          <div
            className="addUser-footer"
            style={{
              display: "flex",
              gap: "12px",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              className="addUser-btn-cancel"
              style={{
                flex: 1,
                 height: "35px",
              }}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="addUser-btn-submit"
              disabled={!formik.dirty || !formik.isValid || loadingOption || formik.isSubmitting}
              style={{
                flex: 1,
                height: "35px",
              }}
            >
              {formik.isSubmitting ? "Inviting..." : submitLabel}
            </button>
          </div>
      </form>
    </div>
  </div>
);
};
